import { create } from 'zustand';

import { authAPI } from '../api/auth';
import { usersAPI } from '../api/users';

/**
 * Authentication store using Zustand
 */
export const useAuthStore = create((set, get) => ({
  // State
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isInitialized: false, // true once initializeAuth has completed
  isLoading: false,
  error: null,

  // Actions
  setUser: (user) => set({ user, isAuthenticated: !!user }),

  setAccessToken: (token) => {
    if (token) {
      localStorage.setItem('accessToken', token);
      set({ accessToken: token, isAuthenticated: true });
    } else {
      localStorage.removeItem('accessToken');
      set({ accessToken: null, isAuthenticated: false });
    }
  },

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  // Register new user
  register: async (userData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.register(userData);
      set({ isLoading: false });
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'Registration failed';
      set({ isLoading: false, error: errorMessage });
      throw new Error(errorMessage);
    }
  },

  // Login
  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.login(credentials);

      // Store access token
      get().setAccessToken(response.accessToken);

      // Fetch user profile
      await get().fetchUser();

      set({ isLoading: false });
      return response;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'Login failed';
      set({ isLoading: false, error: errorMessage });
      throw new Error(errorMessage);
    }
  },

  // Logout
  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      get().setAccessToken(null);
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  // Fetch current user
  fetchUser: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await usersAPI.getCurrentUser();
      set({ user: response.user, isAuthenticated: true, isLoading: false });
      return response.user;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch user';
      set({ isLoading: false, error: errorMessage, isAuthenticated: false });
      get().setAccessToken(null);
      throw new Error(errorMessage);
    }
  },

  // Update profile
  updateProfile: async (updates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await usersAPI.updateProfile(updates);
      set({ user: response.user, isLoading: false });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      set({ isLoading: false, error: errorMessage });
      throw new Error(errorMessage);
    }
  },

  // Initialize auth state on app load.
  // Always attempts a token refresh first so sessions survive server restarts
  // and expired access tokens without requiring the user to log in again.
  initializeAuth: async () => {
    const existingToken = localStorage.getItem('accessToken');

    if (!existingToken) {
      set({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
      });
      return;
    }

    set({
      accessToken: existingToken,
      isAuthenticated: true,
      isLoading: true,
      isInitialized: true,
    });

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 3000);

    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        signal: controller.signal,
      });

      window.clearTimeout(timeoutId);

      if (!res.ok) throw new Error('No session');

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Refresh response was not JSON');
      }

      const body = await res.json();

      if (!body.accessToken) throw new Error('No session');

      localStorage.setItem('accessToken', body.accessToken);
      set({ accessToken: body.accessToken, isAuthenticated: true });

      await get().fetchUser();
    } catch {
      localStorage.removeItem('accessToken');
      set({
        user: null,
        accessToken: null,
        isAuthenticated: false,
      });
    } finally {
      window.clearTimeout(timeoutId);
      set({ isLoading: false, isInitialized: true });
    }
  },
}));
